import { useCallback, useSyncExternalStore } from "react";

function readMatch(query: string): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
}

/**
 * Whether a CSS media query matches, and a re-render whenever that changes.
 *
 * Read synchronously on the first render (`useSyncExternalStore`) rather than
 * in an effect: a layout that renders as "wide" for one frame and then corrects
 * itself animates between the two on every page load — the sidebar rail used to
 * be the case in point. Media queries resolve `rem` against the browser's 16px,
 * not the app's 18px root, so write breakpoints in px.
 */
export function useMediaQuery(query: string): boolean {
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
            const mql = window.matchMedia(query);
            mql.addEventListener("change", onChange);
            return () => mql.removeEventListener("change", onChange);
        },
        [query],
    );

    return useSyncExternalStore(subscribe, () => readMatch(query), () => false);
}
